/**
 * ISO 21434 Compliance — summary (overall score, compliance level,
 * gap counts), section scores, gaps list, sections complete /
 * below threshold, recommendations. Pulls from
 * `formatted.iso_21434_compliance`. Always read-only.
 *
 * The structured presenter populates every field with defaults
 * (null / 0 / []) even when the underlying analysis agent didn't
 * produce a compliance assessment — so all SubSections render
 * unconditionally; empty values fall back to "—" / "No items.".
 */
import React from 'react';
import StatusPill from '../../../../components/ui/StatusPill/StatusPill';
import { TypographyMuted, TypographyList, TypographySmall } from '../../../../components/ui/Typography';
import { SectionCard, SubSection, Field, ObjectListField } from './parts';
import styles from './Iso21434Compliance.module.css';

/** Inline bullet list — used by the ISO tab to render list values
 *  directly under a SubSection title without the StringListField's
 *  redundant inner Field label (which would duplicate the SubSection
 *  title in this context). Lists render through TypographyList (the
 *  Step 1 list convention); empty falls back to muted text. */
function BulletList({ items, emptyText = '—' }: { items: unknown[]; emptyText?: string }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <TypographyMuted>{emptyText}</TypographyMuted>;
  }
  return (
    <TypographyList>
      {items.map((item, i) => (
        <li key={i}><TypographySmall>{String(item)}</TypographySmall></li>
      ))}
    </TypographyList>
  );
}

const severityToneFor = (label: unknown): 'error' | 'warning' | 'info' | 'neutral' => {
  const v = String(label || '').toLowerCase();
  if (v.includes('critical')) return 'error';
  if (v.includes('high')) return 'warning';
  if (v.includes('medium')) return 'info';
  if (v.includes('low')) return 'neutral';
  return 'neutral';
};

interface Iso21434ComplianceProps {
  data?: any;
}

export default function Iso21434Compliance({ data }: Iso21434ComplianceProps) {
  const iso = data ?? {};
  const score = iso.overall_score;
  const scorePct =
    typeof score === 'number' ? (score <= 1 ? Math.round(score * 100) : Math.round(score)) : null;

  const gaps = Array.isArray(iso.gaps) ? iso.gaps : [];
  const sectionScores = iso.section_scores ?? {};
  const sectionsComplete = iso.sections_complete ?? [];
  const sectionsBelow = iso.sections_below_threshold ?? [];
  const recommendations = iso.recommendations ?? [];

  // When compliance was never assessed, gap counts of 0 are misleading
  // (looks like a green pass). Show "—" instead — mirrors the
  // "Not Assessed" label on the compliance-level pill.
  const notAssessed = !iso.overall_score && String(iso.compliance_level || '').toLowerCase().includes('not');
  const totalGapsLabel = notAssessed ? '—' : String(iso.total_gaps ?? 0);
  const criticalGapsLabel = notAssessed ? '—' : String(iso.critical_gaps ?? 0);

  const gapColumns = [
    { key: 'section', label: 'Section' },
    { key: 'description', label: 'Description' },
    {
      key: 'severity',
      label: 'Severity',
      render: (v) => (v ? <StatusPill tone={severityToneFor(v)} label={String(v)} size="sm" /> : null),
    },
  ];

  return (
    <SectionCard title="ISO 21434 Compliance" id="iso_21434_compliance">
      <SubSection title="Summary">
        <div className={styles.summaryGrid}>
          <div className={styles.scoreCard}>
            <div className={styles.scoreNumber}>
              {scorePct == null ? '—' : scorePct}
              {scorePct != null && <span className={styles.scoreUnit}>%</span>}
            </div>
            <TypographyMuted>Overall score</TypographyMuted>
          </div>
          <div className={styles.scoreCard}>
            <div className={styles.scoreText}>{iso.compliance_level || 'Not Assessed'}</div>
            <TypographyMuted>Compliance level</TypographyMuted>
          </div>
          <div className={styles.scoreCard}>
            <div className={styles.scoreNumber}>{totalGapsLabel}</div>
            <TypographyMuted>Total gaps</TypographyMuted>
          </div>
          <div className={styles.scoreCard}>
            <div className={styles.scoreNumber}>{criticalGapsLabel}</div>
            <TypographyMuted>Critical gaps</TypographyMuted>
          </div>
        </div>
      </SubSection>

      <SubSection title="Section scores">
        {Object.keys(sectionScores).length === 0 ? (
          <TypographyMuted>—</TypographyMuted>
        ) : (
          Object.entries(sectionScores).map(([key, value]) => (
            <Field key={key} label={key.replace(/_/g, ' ')} value={typeof value === 'number' ? `${Math.round(value * 100)}%` : String(value)} />
          ))
        )}
      </SubSection>

      <SubSection title="Gaps" count={gaps.length}>
        <ObjectListField columns={gapColumns} rows={gaps} isEditing={false} path={[]} />
      </SubSection>

      <SubSection title="Sections complete" count={sectionsComplete.length}>
        <BulletList items={sectionsComplete} emptyText="—" />
      </SubSection>

      <SubSection title="Sections below threshold" count={sectionsBelow.length}>
        <BulletList items={sectionsBelow} emptyText="—" />
      </SubSection>

      <SubSection title="Recommendations" count={recommendations.length}>
        <BulletList items={recommendations} emptyText="No items." />
      </SubSection>
    </SectionCard>
  );
}
